import React, { useState } from 'react';
import { FinancialDecisionSuggestion } from '../types';
import FinancialDecisionCard from './FinancialDecisionCard';
import '../styles/FinancialDecisionDashboard.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faSyncAlt, faSpinner, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

interface FinancialDecisionDashboardProps {
  decisions: FinancialDecisionSuggestion[];
  loading: boolean;
  onGenerateSuggestions: () => void;
  totalRevenue: number;
  totalProfit: number;
  totalTaxes: number;
  monthlyProfit: number;
}

type SortOption = 'priority' | 'impact' | 'recent';
type FilterOption = 'all' | 'critical' | 'high' | 'medium' | 'low';

const FinancialDecisionDashboard: React.FC<FinancialDecisionDashboardProps> = ({
  decisions,
  loading,
  onGenerateSuggestions,
  totalRevenue,
  totalProfit,
  totalTaxes,
  monthlyProfit,
}) => {
  const [sortBy, setSortBy] = useState<SortOption>('priority');
  const [filterBy, setFilterBy] = useState<FilterOption>('all');

  const getPriorityScore = (desc: string): number => {
    const lowerDesc = desc.toLowerCase();
    if (lowerDesc.includes('urgente') || lowerDesc.includes('crítico')) return 4;
    if (lowerDesc.includes('importante') || lowerDesc.includes('recomenda-se')) return 3;
    if (lowerDesc.includes('considerar') || lowerDesc.includes('avaliar')) return 2;
    return 1;
  };

  const getPriorityLevel = (description: string): FilterOption => {
    const score = getPriorityScore(description);
    if (score === 4) return 'critical';
    if (score === 3) return 'high';
    if (score === 2) return 'medium';
    return 'low';
  };

  const filteredDecisions = decisions.filter(d => {
    if (filterBy === 'all') return true;
    return getPriorityLevel(d.description) === filterBy;
  });

  const sortedDecisions = [...filteredDecisions].sort((a, b) => {
    if (sortBy === 'priority') {
      return getPriorityScore(b.description) - getPriorityScore(a.description);
    }
    if (sortBy === 'impact') {
      const impactA = a.impact?.includes('positivo') ? 1 : -1;
      const impactB = b.impact?.includes('positivo') ? 1 : -1;
      return impactB - impactA;
    }
    return 0;
  });

  const criticalCount = filteredDecisions.filter(d => getPriorityLevel(d.description) === 'critical').length;
  const highCount = filteredDecisions.filter(d => getPriorityLevel(d.description) === 'high').length;

  return (
    <div className="financial-decision-dashboard">
      {/* Header */}
      <div className="fdd-header">
        <div className="fdd-title-section">
          <h2 className="fdd-title"> Análise de Decisões Financeiras com IA</h2>
          <p className="fdd-subtitle">Recomendações personalizadas baseadas em sua situação financeira</p>
        </div>

      </div>

      {/* Financial Overview Cards */}
      {decisions.length > 0 && (
        <div className="fdd-overview-cards">
          <div className="overview-card">
            <span className="overview-icon"></span>
            <div>
              <p className="overview-label">Receita</p>
              <p className="overview-value">{formatCurrency(totalRevenue)}</p>
            </div>
          </div>
          <div className="overview-card">
            <span className="overview-icon"></span>
            <div>
              <p className="overview-label">Lucro</p>
              <p className="overview-value green">{formatCurrency(totalProfit)}</p>
            </div>
          </div>
          <div className="overview-card">
            <span className="overview-icon"></span>
            <div>
              <p className="overview-label">Impostos</p>
              <p className="overview-value red">{formatCurrency(totalTaxes)}</p>
            </div>
          </div>
          <div className="overview-card highlight">
            <span className="overview-icon"></span>
            <div>
              <p className="overview-label">Lucro Mensal</p>
              <p className="overview-value blue">{formatCurrency(monthlyProfit)}</p>
            </div>
          </div>
        </div>
      )}

      {decisions.length > 0 && (
        <>
          {/* Alerts and Controls */}
          <div className="fdd-controls">
            <div className="fdd-alerts">
              {criticalCount > 0 && (
                <div className="alert alert-critical">
                  <FontAwesomeIcon icon={faCircle} style={{color:'#ef4444'}}/> {criticalCount} decisão{criticalCount > 1 ? 's' : ''} crítica{criticalCount > 1 ? 's' : ''}
                </div>
              )}
              {highCount > 0 && (
                <div className="alert alert-high">
                  <FontAwesomeIcon icon={faCircle} style={{color:'#f97316'}}/> {highCount} recomendação{highCount > 1 ? 's' : ''} de alta prioridade
                </div>
              )}
            </div>

            <div className="fdd-filter-sort">
              <div className="control-group">
                <label>Filtrar:</label>
                <select
                  value={filterBy}
                  onChange={e => setFilterBy(e.target.value as FilterOption)}
                  className="select-control"
                >
                  <option value="all">Todas ({decisions.length})</option>
                  <option value="critical">Críticas</option>
                  <option value="high">Alta Prioridade</option>
                  <option value="medium">Médio</option>
                  <option value="low">Baixa Prioridade</option>
                </select>
              </div>

              <div className="control-group">
                <label>Ordenar por:</label>
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value as SortOption)}
                  className="select-control"
                >
                  <option value="priority">Prioridade</option>
                  <option value="impact">Impacto</option>
                  <option value="recent">Mais Recente</option>
                </select>
              </div>

              <button
                onClick={onGenerateSuggestions}
                disabled={loading}
                className="fdd-refresh-btn"
                title="Regenerar análise"
              >
                {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faSyncAlt} />} Atualizar
              </button>
            </div>
          </div>

          {/* Decisions List */}
          <div className="fdd-decisions-list">
            {sortedDecisions.length > 0 ? (
              <>
                <div className="decisions-count">
                  Mostrando {sortedDecisions.length} de {decisions.length} decisões
                </div>
                {sortedDecisions.map((decision, index) => (
                  <FinancialDecisionCard key={index} decision={decision} index={index} />
                ))}
              </>
            ) : (
              <div className="fdd-empty-state">
                <p>Nenhuma decisão nesta categoria</p>
              </div>
            )}
          </div>

          {/* Footer Disclaimer */}
          <div className="fdd-footer">
            <p>
              <strong><FontAwesomeIcon icon={faExclamationTriangle} /> Aviso Importante:</strong> As sugestões de IA são baseadas em análise de dados e devem ser
              usadas como referência. Sempre consulte um assessor financeiro ou contador antes de tomar decisões
              financeiras significativas.
            </p>
          </div>
        </>
      )}

      {/* Loading State */}
      {loading && decisions.length === 0 && (
        <div className="fdd-loading">
          <div className="loading-spinner"></div>
          <p>Analisando sua situação financeira...</p>
          <p className="loading-subtitle">Isso pode levar alguns segundos</p>
        </div>
      )}
    </div>
  );
};

// Helper function
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

export default FinancialDecisionDashboard;
